import { entitySelectionPayload } from '../../../../shared/utils/entitySelection';
import { useRef, useState } from 'react';
import { parseEuroToCents, splitInstallments } from '../../../../shared/utils/installments';
import { createInvoice } from '@/modules/accounting/services';
import {
  getInitialInvoiceForm,
  getInitialInvoiceInstallments,
} from '@/modules/accounting/utils';
import { toast } from '../../../../shared/hooks/use-toast';

function findClientById(clients, clientId) {
  if (clientId === null || clientId === undefined || clientId === '') {
    return null;
  }

  return clients.find((item) => String(item.id) === String(clientId)) || null;
}

function normalizePositiveInteger(value, fallback = 1) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
}

function derivePaymentPlanFromPrefill(prefill = {}, totalAmount = '') {
  const installmentDueDates = Array.isArray(prefill.installmentDueDates)
    ? prefill.installmentDueDates.filter(Boolean)
    : Array.isArray(prefill.dilazionamento?.scadenze)
      ? prefill.dilazionamento.scadenze.filter(Boolean)
      : [];
  const installmentCount = normalizePositiveInteger(
    prefill.installmentCount ?? prefill.dilazionamento?.rate,
    Math.max(1, installmentDueDates.length)
  );
  const paymentExpectedDate =
    prefill.paymentExpectedDate ||
    prefill.paymentDate ||
    prefill.dataPagamento ||
    installmentDueDates[0] ||
    '';
  const type = installmentCount > 1 ? 'rateizzato' : 'unico';
  const numericAmount = Number(totalAmount);
  const installmentAmount = Number(prefill.installmentAmount ?? prefill.dilazionamento?.importoRata);

  return {
    type,
    paymentExpectedDate,
    installmentCount,
    installmentDueDates,
    inheritedPaymentPlan: prefill.completedDealId !== null && prefill.completedDealId !== undefined,
    installments: buildInheritedInstallments({
      type,
      totalAmount: Number.isFinite(numericAmount) ? numericAmount : 0,
      installmentCount,
      installmentAmount: Number.isFinite(installmentAmount) ? installmentAmount : 0,
      installmentAmounts: prefill.installmentAmounts || prefill.dilazionamento?.importiRate?.map((cents) => cents / 100),
      installmentDueDates,
      paymentExpectedDate,
    }),
  };
}

function buildInheritedInstallments({
  type,
  totalAmount,
  installmentCount,
  installmentAmount,
  installmentAmounts,
  installmentDueDates,
  paymentExpectedDate,
}) {
  if (type !== 'rateizzato') {
    return [{ amount: '', dueDate: paymentExpectedDate || '' }];
  }

  const cents = parseEuroToCents(totalAmount) || 0;
  const amounts = installmentAmounts?.length === installmentCount
    ? installmentAmounts.map(parseEuroToCents)
    : installmentAmount > 0
      ? Array.from({ length: installmentCount }, (_, index) => index === installmentCount - 1
          ? cents - Math.round(installmentAmount * 100) * (installmentCount - 1)
          : Math.round(installmentAmount * 100))
      : splitInstallments(cents, installmentCount);
  return amounts.map((amount, index) => ({
    amount: String(amount / 100),
    dueDate: installmentDueDates[index] || paymentExpectedDate || '',
  }));
}

export function buildInvoicePrefillState(prefill = null, clients = []) {
  const initialForm = getInitialInvoiceForm();

  if (!prefill) {
    return {
      form: initialForm,
      prefillClientWarning: '',
    };
  }

  const matchedClient = findClientById(clients, prefill.clientId);
  const hasPrefilledClientId =
    prefill.clientId !== null &&
    prefill.clientId !== undefined &&
    prefill.clientId !== '';
  const prefilledClientName = (
    prefill.client ||
    prefill.company ||
    prefill.businessName ||
    ''
  ).trim();
  const hasPrefilledAmount =
    prefill.totalAmount !== null &&
    prefill.totalAmount !== undefined &&
    prefill.totalAmount !== '' &&
    Number.isFinite(Number(prefill.totalAmount));
  const normalizedAmount = hasPrefilledAmount
    ? String(prefill.totalAmount)
    : initialForm.totalAmount;
  const hasLegacyClientReference =
    (prefill.client || '').trim() !== '' ||
    (prefill.company || '').trim() !== '' ||
    (prefill.businessName || '').trim() !== '';
  const prefillWarning =
    hasPrefilledClientId && !matchedClient
      ? 'Cliente precompilato non disponibile nel catalogo corrente. Verifica il riferimento cliente prima di creare la fattura.'
      : prefill.clientId == null && hasLegacyClientReference
        ? 'Riferimento cliente legacy senza clientId. Seleziona manualmente il cliente corretto.'
        : '';

  const paymentPlan = derivePaymentPlanFromPrefill(prefill, normalizedAmount);

  return {
    form: {
      ...initialForm,
      ...entitySelectionPayload(prefill),
      client: matchedClient?.name || (hasPrefilledClientId ? prefilledClientName : '') || initialForm.client,
      clientId: matchedClient?.id ?? prefill.clientId ?? initialForm.clientId,
      assignment: (prefill.assignment || '').trim(),
      completedDealId: prefill.completedDealId ?? initialForm.completedDealId,
      initialPaymentStatus: prefill.initialPaymentStatus || prefill.completedDealPaymentStatus || initialForm.initialPaymentStatus,
      paymentExpectedDate: paymentPlan.paymentExpectedDate,
      installmentCount: paymentPlan.installmentCount,
      installmentDueDates: paymentPlan.installmentDueDates,
      inheritedPaymentPlan: paymentPlan.inheritedPaymentPlan,
      type: paymentPlan.type,
      totalAmount: normalizedAmount,
      notes: (prefill.notes || '').trim(),
      customFields: { ...(prefill.customFields || {}) },
    },
    installments: paymentPlan.installments,
    prefillClientWarning: prefillWarning,
  };
}

export function allocateInvoiceInstallments(rows, totalAmount, distribute = false) {
  const total = parseEuroToCents(totalAmount) || 0;
  const last = rows.length - 1;
  const manualTotal = rows.reduce((sum, row, index) => sum + (index < last && row.manualAmount ? parseEuroToCents(row.amount) || 0 : 0), 0);
  const automaticCount = rows.filter((row, index) => index === last || !row.manualAmount).length;
  const base = Math.floor((total - manualTotal) / Math.max(1, automaticCount));
  const next = rows.map((row, index) => ({ ...row,
    ...(distribute && index < last && !row.manualAmount ? { amount: (base / 100).toFixed(2) } : {}),
  }));
  if (last >= 0) next[last] = { ...next[last], manualAmount: false,
    amount: ((total - next.slice(0, -1).reduce((sum, row) => sum + (parseEuroToCents(row.amount) || 0), 0)) / 100).toFixed(2),
  };
  return next;
}

export function useCreateInvoiceForm({ clients, onCreate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(getInitialInvoiceForm());
  const [installments, setInstallments] = useState(getInitialInvoiceInstallments());
  const [prefillClientWarning, setPrefillClientWarning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const inheritedPaymentPlanRef = useRef(false);

  function buildPrefilledForm(prefill = null) {
    return buildInvoicePrefillState(prefill, clients);
  }

  function resetForm(prefill = null) {
    const nextState = buildPrefilledForm(prefill);
    inheritedPaymentPlanRef.current = nextState.form.inheritedPaymentPlan === true;
    setForm(nextState.form);
    setPrefillClientWarning(nextState.prefillClientWarning);
    setInstallments(nextState.form.inheritedPaymentPlan ? nextState.installments : getInitialInvoiceInstallments());
  }

  function openDialog(prefill = null) {
    resetForm(prefill);
    setIsOpen(true);
  }

  function closeDialog() {
    if (isSubmittingRef.current) return;
    setIsOpen(false);
    resetForm();
  }

  function updateField(field, value) {
    if (inheritedPaymentPlanRef.current && field === 'type') {
      return;
    }
    if (field === 'totalAmount') {
      setInstallments((prev) => allocateInvoiceInstallments(prev, value, true));
    }
    if (field === 'type' && value === 'rateizzato') {
      setInstallments((prev) => allocateInvoiceInstallments(prev, form.totalAmount, true));
    }
    setForm((prev) => (prev.inheritedPaymentPlan && field === 'type'
      ? prev
      : {
          ...prev,
          [field]: value,
        }));
  }

  function handleClientChange(clientId) {
    const client = findClientById(clients, clientId);

    setForm((prev) => ({
      ...prev,
      client: client?.name || '',
      clientId: client?.id || null,
    }));
    setPrefillClientWarning('');
  }

  function addInstallment() {
    if (inheritedPaymentPlanRef.current) return;
    setInstallments((prev) => allocateInvoiceInstallments([...prev, { amount: '', dueDate: '' }], form.totalAmount, true));
  }

  function removeInstallment(index) {
    if (inheritedPaymentPlanRef.current) return;
    setInstallments((prev) => prev.length > 1 ? allocateInvoiceInstallments(prev.filter((_, itemIndex) => itemIndex !== index), form.totalAmount, true) : prev);
  }

  function updateInstallment(index, field, value) {
    if (inheritedPaymentPlanRef.current) return;
    setInstallments((prev) => {
      if (field === 'amount' && index === prev.length - 1) return prev;
      const next = prev.map((installment, itemIndex) => itemIndex === index
        ? { ...installment, [field]: value, ...(field === 'amount' ? { manualAmount: true } : {}) }
        : installment);
      return field === 'amount' ? allocateInvoiceInstallments(next, form.totalAmount) : next;
    });
  }

  async function submit() {
    if (isSubmittingRef.current) {
      return false;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await createInvoice(form, installments);
      if (!result.ok) {
        toast({ title: 'Fattura non creata', description: result.message, variant: 'destructive' });
        return false;
      }

      onCreate(result.data);
      setIsOpen(false);
      resetForm();
      return true;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  return {
    isOpen,
    isSubmitting,
    form,
    installments,
    prefillClientWarning,
    openDialog,
    closeDialog,
    updateField,
    handleClientChange,
    addInstallment,
    removeInstallment,
    updateInstallment,
    submit,
  };
}
